/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import React from 'react'
import { RouteComponentProps, Route } from 'react-router-dom'
import { FormFeedback, Input, Spinner, Form, Row, Col, Label, Button } from 'reactstrap'
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory, { PaginationProvider, PaginationListStandalone, SizePerPageDropdownStandalone, PaginationTotalStandalone } from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import './AccountFacilityDetail.scss'
import { observer, Observer } from 'mobx-react'
import { container } from '../../arc/configuration/ServiceLocator'
import { IUserSessionStateManager } from '../../arc/services/UserSessionStateManager/contracts'
import { IAccountFacilityGridStateManager, IUserFacilityDetailRow, IUser } from '../../services/AccountFacilityStateManager/contracts'
import overlayFactory from 'react-bootstrap-table2-overlay'
import { TopPart } from '../TopPart/TopPart'

export interface IUserFacilitiesProps extends RouteComponentProps<any> {
	// the id of the item to create (can be zero for new item)
	id: string

	// the external state for the user
	usersessionStateManager: IUserSessionStateManager

	AccountFacilityStateManager: IAccountFacilityGridStateManager
}

export interface IAccountFacilityUserDetailState {
	users: any
	userSelection: any
	disableCheckBoxForCorporateUser: any
	isLoading: boolean
	IsPageLoading: boolean
	checkAllFor: boolean
	isSearching: boolean
}

@observer
export class AccountFacilityDetail extends React.Component<IUserFacilitiesProps, IAccountFacilityUserDetailState> {
	constructor(props: Readonly<IUserFacilitiesProps>) {
		super(props)
		this.changeSelectionForAllUsers = this.changeSelectionForAllUsers.bind(this)
		this.state = {
			users: null,
			userSelection: [],
			disableCheckBoxForCorporateUser: [],
			isLoading: false,
			IsPageLoading: true,
			checkAllFor: false,
			isSearching: false,
		}
	}
	NoDataIndication = (): any => <div className="no-data">{this.state.isLoading ? <Spinner animation="border" role="status" /> : <span>No result found</span>}</div>

	componentDidMount(): void {
		const suppliedId = parseInt(this.props.match.params.id) || 0
		this.props.AccountFacilityStateManager.getAuthorizationStatus().then(response => {
			if (response === true) {
				this.props.AccountFacilityStateManager.loadselectedAccountFacilityById(suppliedId)
					.catch(error => {
						console.log(error)
					})
					.then(() => {
						this.setState({
							users: this.props.AccountFacilityStateManager.availableUser,
							IsPageLoading: false,
							userSelection: this.props.AccountFacilityStateManager.availableUserSelections,
							disableCheckBoxForCorporateUser: this.props.AccountFacilityStateManager.availableCorporateUser,
						})
					})
					.then(() => {
						this.props.AccountFacilityStateManager.loadFacilityUserGridResults()
							.catch(error => console.error(error))
							.then(() => {
								this.props.AccountFacilityStateManager.loadAvailableStates()
									.catch(error => {
										console.log(error)
									})
									.then(() => {
										const statesFromApi = this.props.AccountFacilityStateManager.availableStates.map(element => {
											return { id: element.id, name: element.name }
										})
										statesFromApi.unshift({ id: 0, name: 'Select State' })
										this.props.AccountFacilityStateManager.availableStates = statesFromApi
									})
							})
					})
			}
		})

		window.scrollTo(0, 0)
	}

	onCancel(): void {
		this.props.history.push('/facilities')
	}

	onSave(): void {
		if (this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isAllFieldsValid() && !this.props.AccountFacilityStateManager.isFacilityNameExists) {
			this.setState({ isSearching: true })
			this.props.AccountFacilityStateManager.saveOrUpdateSelectedAccountFacility()
				.then(() => this.props.history.push('/facilities'))
				.then(() => {
					this.setState({
						isSearching: false,
					})
				})
				.catch(() => {
					this.setState({
						isSearching: false,
					})
				})
		}
	}

	checkChangedFor(event: any, user: IUser, value: boolean): void {
		this.props.AccountFacilityStateManager.setUserSelectionSelectedValueFor(user, value)

		event.target.checked = value
		const userSelections = this.state.userSelection

		userSelections.forEach((element: { id: number; isSelected: any }) => {
			if (element.id === user.id) {
				element.isSelected = event.target.checked
			}
		})

		this.setState({
			userSelection: userSelections,
		})
	}

	changeSelectionForAllUsers(): void {
		const currentState = !this.state.checkAllFor

		const userSelectionUsersFor = this.state.userSelection

		const checkForCorporateUser = this.state.disableCheckBoxForCorporateUser

		let count = 0
		userSelectionUsersFor.forEach(element => {
			if (element.isSelected) {
				count = count + 1
			}
		})

		if (count === userSelectionUsersFor.length) {
			userSelectionUsersFor.forEach((element: { isSelected: boolean; id: number }) => {
				const findFor = checkForCorporateUser.find((x: { id: number }) => x.id === element.id)
				if (!findFor) {
					element.isSelected = false
				}
			})
		} else {
			userSelectionUsersFor.forEach((element: { isSelected: boolean; id: number }) => {
				const findFor = checkForCorporateUser.find((x: { id: number }) => x.id === element.id)
				if (!findFor) element.isSelected = currentState
			})
		}
		this.setState({
			checkAllFor: currentState,
			userSelection: userSelectionUsersFor,
		})
	}

	handleTableChange = (type: string, args: any): void => {
		this.setState({ isLoading: true })
		this.props.AccountFacilityStateManager.setUserFacilityGridCriteria({
			sortOrder: args.sortOrder ? args.sortOrder : this.props.AccountFacilityStateManager.userFacilityGridCriteria.sortOrder,
			sortField: args.sortField ? args.sortField : this.props.AccountFacilityStateManager.userFacilityGridCriteria.sortField,
			page: args.page,
			sizePerPage: args.sizePerPage,
		})
			.then(() => {
				this.setState({ isLoading: false })
			})
			.catch(error => {
				this.setState({ isLoading: false })
				alert(error)
			})
	}

	ShowOrHideActiveCheckBox(): any {
		if (typeof this.props.AccountFacilityStateManager.selectedAccountFacility !== 'undefined' && this.props.AccountFacilityStateManager.selectedAccountFacility.id === 0) {
			return null
		} else {
			return (
				<Col id="Active">
					<Label>
						Active<span className="red">*</span>
					</Label>
					&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
					<Input
						type="checkbox"
						onChange={e => {
							return this.props.AccountFacilityStateManager.setSelectedItem({ ...this.props.AccountFacilityStateManager.selectedAccountFacility, active: e.target.checked })
						}}
						checked={this.props.AccountFacilityStateManager.selectedAccountFacility.active}
					/>
				</Col>
			)
		}
	}

	render(): React.ReactNode {
		if (this.props.AccountFacilityStateManager.authorizedForFacility) {
			if (this.state.IsPageLoading) {
				if (typeof this.props.AccountFacilityStateManager.selectedAccountFacility !== 'undefined' && this.props.AccountFacilityStateManager.selectedAccountFacility.id !== 0) {
					return (
						<div>
							<TopPart pagesource="editfacility" />
							<div className="row  tab-content">
								<div className="spinnerPage">
									<Spinner animation="border" role="status" />
								</div>
							</div>
						</div>
					)
				} else {
					return (
						<div>
							<TopPart pagesource="newfacility" />
							<div className="row  tab-content">
								<div className="spinnerPage">
									<Spinner animation="border" role="status" />
								</div>
							</div>
						</div>
					)
				}
			} else {
				if (!this.props.AccountFacilityStateManager.isSelectedFacilityIdNotExists) {
					return (
						<div>
							{typeof this.props.AccountFacilityStateManager.selectedAccountFacility !== 'undefined' && this.props.AccountFacilityStateManager.selectedAccountFacility.id !== 0 ? <TopPart pagesource="editfacility" /> : <TopPart pagesource="newfacility" />}
							<div className="row  tab-content">
								<div className="roles-detail col">
									<Form>
										<div className="form-block editNpi">
											<div className="row-fluid form-header">
												<div className="span12">
													<div className="pull-right marginRight70">*Required Field</div>
													<h2>Facility Details</h2>
												</div>
											</div>
											<div className="boxBody backgroundWhite">
												<div className="boxContent">
													<Row>
														<Col>
															<Input type="hidden" className="rounded-lg w-75" value={this.props.AccountFacilityStateManager.selectedAccountFacility.customerEntityFacilityStatusId}></Input>
														</Col>
													</Row>
													<Row>
														<Col>
															<Label className="entryPrompt w-75">
																Facility Name<span className="red">*</span>
															</Label>
															<Input type="text" className="rounded-lg w-75" placeholder="Enter the Facility Name" onChange={(e: React.ChangeEvent<HTMLInputElement>): void => this.props.AccountFacilityStateManager.setSelectedItem({ ...this.props.AccountFacilityStateManager.selectedAccountFacility, facilityName: e.target.value })} value={this.props.AccountFacilityStateManager.selectedAccountFacility.facilityName} valid={this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('facilityName') || !this.props.AccountFacilityStateManager.isFacilityNameExists} invalid={!this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('facilityName') || this.props.AccountFacilityStateManager.isFacilityNameExists}></Input>
															{this.props.AccountFacilityStateManager.isFacilityNameExists ? <FormFeedback>Facility name already exists.</FormFeedback> : <></>}
															<FormFeedback>{this.props.AccountFacilityStateManager.validateSelectedAccountFacility.getMessage('facilityName')}</FormFeedback>
														</Col>
													</Row>
													<Row>
														<Col>
															<Label className="entryPrompt w-75">
																NPI<span className="red">*</span>
															</Label>
															<Input type="text" className="rounded-lg w-75" placeholder="Enter the NPI" onChange={e => this.props.AccountFacilityStateManager.setSelectedItem({ ...this.props.AccountFacilityStateManager.selectedAccountFacility, npi: e.target.value })} value={this.props.AccountFacilityStateManager.selectedAccountFacility.npi} valid={this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('npi') || !this.props.AccountFacilityStateManager.isNpiExists} invalid={!this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('npi') || this.props.AccountFacilityStateManager.isNpiExists}></Input>
															{this.props.AccountFacilityStateManager.isNpiExists ? <FormFeedback>Npi already exists.</FormFeedback> : <></>}
															<FormFeedback>{this.props.AccountFacilityStateManager.validateSelectedAccountFacility.getMessage('npi')}</FormFeedback>
														</Col>
													</Row>
													<Row>
														<Col>
															<Label className="entryPrompt w-75">
																Practice Location State<span className="red">*</span>
															</Label>
															<Input type="select" name="states" className="rounded-lg w-75 non-uniform" id="states" onChange={e => this.props.AccountFacilityStateManager.setSelectedItem({ ...this.props.AccountFacilityStateManager.selectedAccountFacility, practiceLocationStateCodeId: parseInt(e.target.value) })} value={this.props.AccountFacilityStateManager.selectedAccountFacility.practiceLocationStateCodeId} valid={this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('practiceLocationStateCodeId')} invalid={!this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('practiceLocationStateCodeId')}>
																{this.props.AccountFacilityStateManager.availableStates.map(c => (
																	<option key={c.id} value={c.id}>
																		{c.name}
																	</option>
																))}
															</Input>
															<FormFeedback>{this.props.AccountFacilityStateManager.validateSelectedAccountFacility.getMessage('practiceLocationStateCodeId')}</FormFeedback>
														</Col>
													</Row>
													<Row>
														<Col>
															<Label className="entryPrompt w-75">Provider ID / PIN (for Medicaid and BCBS plans in designated states)</Label>
															<Input type="text" className="rounded-lg w-75" placeholder="Enter the Provider ID / PIN" onChange={e => this.props.AccountFacilityStateManager.setSelectedItem({ ...this.props.AccountFacilityStateManager.selectedAccountFacility, primaryPin: e.target.value })} value={this.props.AccountFacilityStateManager.selectedAccountFacility.primaryPin !== null ? this.props.AccountFacilityStateManager.selectedAccountFacility.primaryPin : ''} valid={this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('primaryPin')} invalid={!this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('primaryPin')}></Input>
															<FormFeedback>{this.props.AccountFacilityStateManager.validateSelectedAccountFacility.getMessage('primaryPin')}</FormFeedback>
														</Col>
													</Row>
													<Row>
														<Col>
															<Label className="entryPrompt w-75">Federal Tax ID</Label>
															<Input type="text" className="rounded-lg w-75" placeholder="Enter the Tax ID" onChange={e => this.props.AccountFacilityStateManager.setSelectedItem({ ...this.props.AccountFacilityStateManager.selectedAccountFacility, federalTaxId: e.target.value })} value={this.props.AccountFacilityStateManager.selectedAccountFacility.federalTaxId !== null ? this.props.AccountFacilityStateManager.selectedAccountFacility.federalTaxId : ''} valid={this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('federalTaxId')} invalid={!this.props.AccountFacilityStateManager.validateSelectedAccountFacility.isFieldValid('federalTaxId')}></Input>
															<FormFeedback>{this.props.AccountFacilityStateManager.validateSelectedAccountFacility.getMessage('federalTaxId')}</FormFeedback>
														</Col>
													</Row>
													<Row>{this.ShowOrHideActiveCheckBox()}</Row>
												</div>
											</div>
										</div>
										<br />
										<div className="form-block editNpi">
											<div className="row-fluid form-header">
												<div className="span12">
													<h2> User Permissions</h2>
												</div>
											</div>
											<div className="boxBody backgroundWhite">
												<div className="boxContent">
													<div className="UserFacility-grid">
														<PaginationProvider
															pagination={paginationFactory({
																custom: true,
																page: this.props.AccountFacilityStateManager.userFacilityGridCriteria.page,
																sizePerPage: this.props.AccountFacilityStateManager.userFacilityGridCriteria.sizePerPage,
																totalSize: this.props.AccountFacilityStateManager.userFacilityGridResults.total,
																showTotal: true,
																sizePerPageList: [
																	{
																		text: '10',
																		value: 10,
																	},
																	{
																		text: '25',
																		value: 25,
																	},
																	{
																		text: '50',
																		value: 50,
																	},
																	{
																		text: '100',
																		value: 100,
																	},
																],
																paginationTotalRenderer: (from: number, to: number, size: number) => (
																	<span className="react-bootstrap-table-pagination-total">
																		{' '}
																		Showing {from} to {to} of {size} Results{' '}
																	</span>
																),
															})}
														>
															{({ paginationProps, paginationTableProps }: any) => {
																return (
																	<div>
																		<div className="clearfix">
																			<span className="float-left" id="showPageSize">
																				<SizePerPageDropdownStandalone {...paginationProps} />
																			</span>
																			<span id="page">
																				<PaginationTotalStandalone {...paginationProps} />
																			</span>
																			<span className="float-right" id="showPagination">
																				<PaginationListStandalone {...paginationProps} />
																			</span>
																		</div>
																		<Observer>
																			{() => (
																				<BootstrapTable
																					keyField="id"
																					classes="dataTable"
																					bootstrap4
																					hover
																					condensed
																					remote
																					noDataIndication={() => <this.NoDataIndication />}
																					onTableChange={(type: string, args) => {
																						return this.handleTableChange(type, args)
																					}}
																					overlay={overlayFactory({ spinner: true, styles: { overlay: (base: any) => ({ ...base, background: 'rgba(255, 0, 0, 0.5)' }) } })}
																					data={this.props.AccountFacilityStateManager.userFacilityGridResults.items.slice()}
																					defaultSorted={[
																						{
																							dataField: this.props.AccountFacilityStateManager.userFacilityGridCriteria.sortField,
																							order: this.props.AccountFacilityStateManager.userFacilityGridCriteria.sortOrder,
																						},
																					]}
																					filter={filterFactory()}
																					{...paginationTableProps}
																					columns={[
																						{
																							dataField: 'users',
																							text: 'User Name',
																							sort: true,
																							headerStyle: () => {
																								return {
																									paddingLeft: '31px',
																									':hover': {
																										textDecoration: 'none',
																									},
																								}
																							},
																							formatter: (cell: JSX.Element, row: IUserFacilityDetailRow): JSX.Element => {
																								return (
																									<div className="userName" style={{ marginLeft: '-70px' }}>
																										{row.users.firstName}&nbsp;
																										{row.users.lastName}
																									</div>
																								)
																							},
																						},
																						{
																							dataField: 'userSelection',
																							text: 'select/unselect all',
																							sort: false,
																							headerStyle: () => {
																								return {
																									width: '28%',
																								}
																							},
																							formatter: (cell: JSX.Element, row: IUserFacilityDetailRow): JSX.Element => {
																								return <Input type="checkbox" id="" className="checkBoxForFacility" checked={typeof this.state.userSelection.find((x: { id: number }) => x.id === row.users.id) === 'undefined' ? false : this.state.userSelection.find((x: { id: number }) => x.id === row.users.id).isSelected} onChange={(e: { target: { checked: boolean } }) => this.checkChangedFor(e, row.users, e.target.checked)} disabled={this.state.disableCheckBoxForCorporateUser.find((x: { id: number }) => x.id === row.users.id)} />
																							},
																							headerEvents: {
																								onClick: this.changeSelectionForAllUsers,
																							},
																						},
																					]}
																				/>
																			)}
																		</Observer>
																		<div className="clearfix">
																			<span className="float-left" id="showPageSizeLower">
																				<PaginationTotalStandalone {...paginationProps} />
																			</span>
																			<span className="float-right" id="showPaginationLower">
																				<PaginationListStandalone {...paginationProps} />
																			</span>
																		</div>
																	</div>
																)
															}}
														</PaginationProvider>
													</div>
												</div>
											</div>
										</div>
										<br />
										<div className="col text-center" id="facilityButtons">
											{this.state.isSearching ? (
												<Button color="btnSaveColor" className="btn save buttonWidth110" disabled>
													<Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
													Submitting
												</Button>
											) : (
												<button type="button" className="btn save buttonWidth110 btn btn-btnSaveColor" onClick={() => this.onSave()}>
													Submit
												</button>
											)}
											&nbsp;&nbsp;&nbsp;
											<button type="button" className="btn cancel buttonWidth110 btn btn-btnCancelColor" onClick={() => this.onCancel()}>
												Cancel
											</button>
										</div>
									</Form>
								</div>
							</div>
						</div>
					)
				} else {
					return (
						<div>
							<TopPart pagesource="facilities" />
							<div className="row  tab-content">
								<div className="width100P">
									<div className="contentBody">
										<div className="col-md-4 alert alert-error">The provided facility does not exists</div>
									</div>
								</div>
							</div>
						</div>
					)
				}
			}
		} else {
			return (
				<div>
					<TopPart pagesource="facilities" />
					<div className="row  tab-content">
						<div className="width100P">
							<div className="contentBody">
								<div className="col-md-6 alert alert-error">You do not have access to this feature. Please contact your administrator.</div>
							</div>
						</div>
					</div>
				</div>
			)
		}
	}
}

export const AccountFacilityDetailConnected = (): any => {
	const accountFacilityDetailStateManager = container.get<IAccountFacilityGridStateManager>('IAccountFacilityGridStateManager')
	const userSessionStateManager = container.get<IUserSessionStateManager>('IUserSessionStateManager')
	return <Route path="/facilities/:id" render={props => <AccountFacilityDetail {...props} id={props.match.params.id} AccountFacilityStateManager={accountFacilityDetailStateManager} usersessionStateManager={userSessionStateManager} />} />
}
